import React, {createContext, useContext, useEffect, useRef, useMemo} from 'react';

import {mainStateMachine} from "../lib/mainStateMachine";
import {useMachine} from "@xstate/react";
import {localStorageKeys as k} from "../index";
import {machine as m} from "../constants/machine.constants";
import {getPlayerScenarioInitData} from "../data/initContext";
import {checkObject} from "../lib/mainStateMachine.helpers";
import {SpeechVoicesContext} from "../../../app/contexts/SpeechVoicesContext";


const ActorContext = createContext();
const StateContext = createContext();

const MainStateMachineProvider = ({playerScenarioObj = {}, children}) => {
    const [state, send, actor] = useMachine(mainStateMachine);
    const isRunning = useRef(false);
    const activePlayerScenarioId = useRef(null);
    const voicesLoaded = useRef(false);
    const latestContextRef = useRef(state.context); // Track latest context for saving
    
    // Update ref whenever context changes
    useEffect(() => {
        latestContextRef.current = state.context;
    }, [state.context]);

    isRunning.current = !state.matches(m.state.idle);
    const {browserVoicesMap, loading} = useContext(SpeechVoicesContext)

    // SAVE ON UNMOUNT
    useEffect(() => {
        const saveProgress = () => {
            const ctx = latestContextRef.current;
            if (ctx && ctx.lessonId) {
                const progressData = {
                    lessonId: ctx.lessonId,
                    scenarioId: ctx.scenarioId,
                    currentIndex: ctx.currentIndex,
                    currentCardIndex: ctx.currentCardIndex,
                    currentScenarioIndex: ctx.currentScenarioIndex
                };
                localStorage.setItem(k.appContext, JSON.stringify(progressData));
            }
        };

        window.addEventListener('beforeunload', saveProgress);
        return () => {
            saveProgress();
            window.removeEventListener('beforeunload', saveProgress);
        };
    }, []);

    // Handle initial private load and scenario changes
    useEffect(() => {
        const loadInitialData = async () => {
            const currentPSStr = JSON.stringify(activePlayerScenarioId.current);
            const newPSStr = JSON.stringify(playerScenarioObj);
            
            if (currentPSStr !== newPSStr) {
                let initData = await getPlayerScenarioInitData(playerScenarioObj);
                
                // CHECK SAVED PROGRESS
                const savedStateStr = localStorage.getItem(k.appContext);
                if (savedStateStr) {
                    const savedProgress = checkObject(savedStateStr);
                    // Restore only if it's the same scenario/lesson
                    if (savedProgress && 
                        savedProgress.scenarioId === playerScenarioObj.scenarioId && 
                        String(savedProgress.lessonId) === String(playerScenarioObj.id)) {
                        
                        // Merge progress into fresh initData
                        initData = { 
                            ...initData, 
                            currentIndex: savedProgress.currentIndex,
                            currentCardIndex: savedProgress.currentCardIndex,
                            currentScenarioIndex: savedProgress.currentScenarioIndex
                        };
                    }
                }

                if (isRunning.current) {
                    send({type: m.events.IDLE});
                }
                
                send({
                    type: m.events.SET_DATA, 
                    context: {
                        ...initData, 
                        browserVoicesMap: !loading ? browserVoicesMap : {}
                    }
                });
                
                activePlayerScenarioId.current = playerScenarioObj;
                voicesLoaded.current = !loading;
            }
        }
        
        loadInitialData();
    }, [playerScenarioObj, send, loading, browserVoicesMap]);

    // Handle asynchronous voice updates
    useEffect(() => {
        if (!loading && !voicesLoaded.current && Object.keys(browserVoicesMap).length > 0) {
            if (state.matches(m.state.idle)) {
                send({
                    type: m.events.SET_DATA, 
                    context: { ...state.context, browserVoicesMap }
                });
            }
            voicesLoaded.current = true;
        }
    }, [loading, browserVoicesMap, send, state.matches, state.context]);

    // STABLE CONTEXT: actor and send never change
    const actorValue = useMemo(() => ({ actor, send }), [actor, send]);

    return (
        <ActorContext.Provider value={actorValue}>
            <StateContext.Provider value={state}>
                {children}
            </StateContext.Provider>
        </ActorContext.Provider>
    );
};


// Returns STABLE actor and send. Use this for useSelector.
const useMainStateMachine = () => {
    return useContext(ActorContext)
}

// Returns REACTIVE state. Use this only when you need full state updates.
const useMainState = () => {
    return useContext(StateContext)
}


export {MainStateMachineProvider, useMainStateMachine, useMainState};